import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fireEvent } from "@/lib/events";
import { queryClient } from "@/lib/queryClient";
import { useAuth } from "./useAuth";
import { useActiveSpace } from "./useActiveSpace";

export interface Collection {
  id: string;
  user_id: string;
  template_id: string | null;
  name: string;
  icon: string;
  color: string;
  entry_count: number;
  last_entry_at: string | null;
  created_at: string;
  space_id: string | null;
  /** NULL = active; set = all required steps completed */
  completed_at: string | null;
  /** NULL = not in active-capture mode; set = auto-tagging memories until this moment.
   *  At most one per user at any time (enforced by partial unique index). */
  active_until?: string | null;
  /** 'guided' = predefined steps only; 'open' = free form; 'hybrid' = both */
  mode: 'guided' | 'open' | 'hybrid';
  /** space_members.id UUIDs the collection is tagged with.
   *  Empty array = no restriction. Owner is always implicitly allowed. */
  participant_member_ids: string[];
  /** children.id UUIDs the collection primarily concerns.
   *  Empty array = no child filter. */
  participant_child_ids: string[];
  /** Permanent locked children — never removable once set. Empty = not locked. */
  locked_child_ids: string[];
  /** Permanent locked members — never removable once set. Empty = not locked. */
  locked_member_ids: string[];
  /** @deprecated use participant_member_ids */
  participant_connection_ids?: string[];
}

export interface CollectionTemplateStep {
  id: string;
  collection_template_id: string;
  content_library_id: string;
  order_index: number;
  required: boolean;
  step_title_override: string | null;
  created_at: string;
  /** Nested card — available when queried with content_library join */
  content_library?: import('./useContentLibrary').ContentLibraryItem;
}

export interface CollectionStepCompletion {
  id: string;
  collection_id: string;
  step_id: string;
  memory_id: string;
  completed_by: string;
  completed_at: string;
  auto_completed: boolean;
}

export interface CollectionMemoryRow {
  id: string;
  added_at: string;
  added_by: string;
  memory: {
    id: string;
    title: string | null;
    memory_type: string;
    created_at: string;
    audio_url: string | null;
    video_url: string | null;
    thumbnail_url: string | null;
    ai_chapter: string | null;
    raw_transcript: string | null;
  } | null;
}

/** Invalidate all collections queries. Call after any mutation that creates/updates/deletes collections. */
export function invalidateCollections() {
  return queryClient.invalidateQueries({ queryKey: ['collections'] });
}

async function fetchCollectionsForSpace(spaceId: string): Promise<Collection[]> {
  const { data: raw } = await supabase
    .from("collections")
    .select("*")
    .eq("space_id", spaceId)
    .order("last_entry_at", { ascending: false, nullsFirst: false });

  const collections = (raw ?? []) as unknown as Collection[];

  return collections;
}

/** Mark a collection as finished. Sets `completed_at = now()` and fires
 *  `collection.completed` so the acorn rules engine can award the template reward. */
export async function finishCollection(
  userId: string,
  collectionId: string,
): Promise<{ error: Error | null }> {
  const { data: collection, error: fetchErr } = await supabase
    .from("collections")
    .select("template_id, completed_at")
    .eq("id", collectionId)
    .maybeSingle();
  if (fetchErr) return { error: fetchErr };
  if (collection?.completed_at) return { error: null };

  const { error } = await supabase
    .from("collections")
    .update({ completed_at: new Date().toISOString() })
    .eq("id", collectionId);
  if (error) return { error };

  let acornReward = 0;
  if (collection?.template_id) {
    const { data: tpl } = await supabase
      .from("collection_templates")
      .select("acorn_reward")
      .eq("id", collection.template_id)
      .maybeSingle();
    acornReward = tpl?.acorn_reward ?? 0;
  }

  await invalidateCollections();
  fireEvent("collection.completed", {
    userId,
    collectionId,
    templateId: collection?.template_id ?? "",
    acornReward,
  });
  return { error: null };
}

/** Reopen a finished collection — clears `completed_at` so the user can add
 *  more memories. Does not reverse any acorns that were awarded on finish. */
export async function reopenCollection(
  userId: string,
  collectionId: string,
): Promise<{ error: Error | null }> {
  const { error } = await supabase
    .from("collections")
    .update({ completed_at: null })
    .eq("id", collectionId);
  if (error) return { error };
  await invalidateCollections();
  return { error: null };
}

/** Hard-delete a collection. The `collection_memories` junction is removed via
 *  ON DELETE CASCADE — memories themselves are unaffected. */
export async function cancelCollection(
  userId: string,
  collectionId: string,
): Promise<{ error: Error | null }> {
  const { error } = await supabase.from("collections").delete().eq("id", collectionId);
  if (error) return { error };
  await invalidateCollections();
  return { error: null };
}

export function useCollections() {
  const { user } = useAuth();
  const { space: activeSpace } = useActiveSpace();
  const qc = useQueryClient();
  const userId = user?.id;
  const spaceId = activeSpace?.id ?? null;

  const query = useQuery({
    queryKey: ['collections', userId, spaceId],
    enabled: !!userId && !!spaceId,
    queryFn: () => fetchCollectionsForSpace(spaceId!),
  });

  const collections = query.data ?? [];

  const now = Date.now();
  const activeCollection =
    collections.find(
      (c) =>
        c.active_until != null &&
        new Date(c.active_until).getTime() > now &&
        c.completed_at == null,
    ) ?? null;

  const refresh = useCallback(() => {
    return qc.invalidateQueries({ queryKey: ['collections', userId, spaceId] });
  }, [qc, userId, spaceId]);

  async function addMemoryToCollection(collectionId: string, memoryId: string): Promise<void> {
    await supabase
      .from("collection_memories")
      .upsert(
        { collection_id: collectionId, memory_id: memoryId, added_by: "manual" },
        { onConflict: "collection_id,memory_id", ignoreDuplicates: true }
      );
  }

  async function getCollectionMemories(collectionId: string): Promise<CollectionMemoryRow[]> {
    const { data } = await supabase
      .from("collection_memories")
      .select(
        "id, added_at, added_by, memory:memories(id, title, memory_type, created_at, audio_url, video_url, thumbnail_url, ai_chapter, raw_transcript)"
      )
      .eq("collection_id", collectionId)
      .order("added_at", { ascending: false });
    return (data ?? []) as unknown as CollectionMemoryRow[];
  }

  // Put this collection into active-capture mode. The partial unique index
  // (`active_until IS NOT NULL AND completed_at IS NULL`) allows only one
  // row per user, so we first clear any other row holding the slot before
  // setting the new one. The predicate is IMMUTABLE (no `now()` check), so
  // expired `active_until` values still occupy the slot until cleared here.
  async function startActiveMode(collectionId: string, durationHours = 72): Promise<void> {
    if (!user) throw new Error("not authenticated");

    const { error: clearErr } = await supabase
      .from("collections")
      .update({ active_until: null })
      .eq("user_id", user.id)
      .not("active_until", "is", null);
    if (clearErr) throw clearErr;

    const activeUntil = new Date(Date.now() + durationHours * 60 * 60 * 1000).toISOString();

    const { error: setErr } = await supabase
      .from("collections")
      .update({ active_until: activeUntil })
      .eq("id", collectionId);
    if (setErr) throw setErr;

    await qc.invalidateQueries({ queryKey: ['collections', userId, spaceId] });

    fireEvent("collection.active_mode.started", { userId: user.id, collectionId, activeUntil });
  }

  async function stopActiveMode(collectionId: string): Promise<void> {
    if (!user) throw new Error("not authenticated");

    const { error } = await supabase
      .from("collections")
      .update({ active_until: null })
      .eq("id", collectionId);
    if (error) throw error;

    await qc.invalidateQueries({ queryKey: ['collections', userId, spaceId] });

    fireEvent("collection.active_mode.ended", { userId: user.id, collectionId });
  }

  return {
    data: query.data,
    collections,
    activeCollection,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
    refresh,
    addMemoryToCollection,
    getCollectionMemories,
    startActiveMode,
    stopActiveMode,
  };
}
